import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

export interface NotificationPreferences {
  id: string;
  user_id?: string;
  customer_id?: string;
  email_enabled: boolean;
  sms_enabled: boolean;
  push_enabled: boolean;
  in_app_enabled: boolean;
  sound_enabled: boolean;
  notify_order_placed: boolean;
  notify_order_ready: boolean;
  notify_order_assigned: boolean;
  notify_order_completed: boolean;
  notify_order_cancelled: boolean;
  notify_payment_received: boolean;
  notify_order_confirmed: boolean;
  notify_order_preparing: boolean;
  notify_order_out_for_delivery: boolean;
  notify_order_delivered: boolean;
  notify_promotions: boolean;
  quiet_hours_enabled: boolean;
  quiet_hours_start?: string;
  quiet_hours_end?: string;
  created_at: string;
  updated_at: string;
}

export function useNotificationPreferences() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Fetch preferences
  const { data: preferences, isLoading, error } = useQuery({
    queryKey: ['notification-preferences', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;

      // First try to get existing preferences
      const { data: existing, error: fetchError } = await supabase
        .from('notification_preferences')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (fetchError) throw fetchError;

      // If preferences exist, return them
      if (existing) {
        return existing as NotificationPreferences;
      }

      // Otherwise, create default preferences
      const { data: created, error: createError } = await supabase
        .from('notification_preferences')
        .insert({
          user_id: user.id,
          email_enabled: true,
          sms_enabled: false,
          push_enabled: true,
          in_app_enabled: true,
          sound_enabled: true,
          notify_order_placed: true,
          notify_order_ready: true,
          notify_order_assigned: true,
          notify_order_completed: true,
          notify_order_cancelled: true,
          notify_payment_received: true,
          notify_order_confirmed: true,
          notify_order_preparing: true,
          notify_order_out_for_delivery: true,
          notify_order_delivered: true,
          notify_promotions: true,
          quiet_hours_enabled: false,
        })
        .select()
        .single();

      if (createError) throw createError;

      return created as NotificationPreferences;
    },
    enabled: !!user?.id,
  });

  // Update preferences mutation
  const updatePreferences = useMutation({
    mutationFn: async (updates: Partial<NotificationPreferences>) => {
      if (!user?.id || !preferences?.id) {
        throw new Error('User not authenticated or preferences not loaded');
      }

      const { data, error } = await supabase
        .from('notification_preferences')
        .update(updates)
        .eq('id', preferences.id)
        .select()
        .single();

      if (error) throw error;
      return data as NotificationPreferences;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(['notification-preferences', user?.id], data);
      toast.success('Notification preferences updated');
    },
    onError: (error) => {
      console.error('Failed to update preferences:', error);
      toast.error('Failed to update notification preferences');
    },
  });

  // Helper function to check if notifications should be sent based on quiet hours
  const isInQuietHours = () => {
    if (!preferences?.quiet_hours_enabled || !preferences.quiet_hours_start || !preferences.quiet_hours_end) {
      return false;
    }

    const now = new Date();
    const currentTime = now.getHours() * 60 + now.getMinutes();
    
    const [startHour, startMin] = preferences.quiet_hours_start.split(':').map(Number);
    const [endHour, endMin] = preferences.quiet_hours_end.split(':').map(Number);
    
    const startTime = startHour * 60 + startMin;
    const endTime = endHour * 60 + endMin;

    // Handle overnight quiet hours (e.g., 22:00 to 07:00)
    if (startTime > endTime) {
      return currentTime >= startTime || currentTime <= endTime;
    }

    return currentTime >= startTime && currentTime <= endTime;
  };

  // Helper to check if a specific notification type is enabled
  const shouldNotify = (notificationType: keyof NotificationPreferences): boolean => {
    if (!preferences) return false;
    if (isInQuietHours()) return false;
    
    return preferences[notificationType] === true;
  };

  return {
    preferences,
    isLoading,
    error,
    updatePreferences: updatePreferences.mutate,
    isUpdating: updatePreferences.isPending,
    shouldNotify,
    isInQuietHours: isInQuietHours(),
  };
}

// Hook for customer notification preferences
export function useCustomerNotificationPreferences(customerId?: string) {
  const queryClient = useQueryClient();

  const { data: preferences, isLoading, error } = useQuery({
    queryKey: ['customer-notification-preferences', customerId],
    queryFn: async () => {
      if (!customerId) return null;

      const { data: existing, error: fetchError } = await supabase
        .from('notification_preferences')
        .select('*')
        .eq('customer_id', customerId)
        .maybeSingle();

      if (fetchError) throw fetchError;

      if (existing) {
        return existing as NotificationPreferences;
      }

      // Create default preferences for customer
      const { data: created, error: createError } = await supabase
        .from('notification_preferences')
        .insert({
          customer_id: customerId,
          email_enabled: true,
          sms_enabled: true,
          push_enabled: true,
          in_app_enabled: true,
          sound_enabled: true,
          notify_order_confirmed: true,
          notify_order_preparing: true,
          notify_order_out_for_delivery: true,
          notify_order_delivered: true,
          notify_promotions: false,
          quiet_hours_enabled: false,
        })
        .select()
        .single();

      if (createError) throw createError;

      return created as NotificationPreferences;
    },
    enabled: !!customerId,
  });

  const updatePreferences = useMutation({
    mutationFn: async (updates: Partial<NotificationPreferences>) => {
      if (!customerId || !preferences?.id) {
        throw new Error('Customer ID or preferences not available');
      }

      const { data, error } = await supabase
        .from('notification_preferences')
        .update(updates)
        .eq('id', preferences.id)
        .select()
        .single();

      if (error) throw error;
      return data as NotificationPreferences;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(['customer-notification-preferences', customerId], data);
      toast.success('Notification preferences updated');
    },
    onError: (error) => {
      console.error('Failed to update customer preferences:', error);
      toast.error('Failed to update notification preferences');
    },
  });

  return {
    preferences,
    isLoading,
    error,
    updatePreferences: updatePreferences.mutate,
    isUpdating: updatePreferences.isPending,
  };
}
